import { defineComponent } from 'vue'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import Button from 'primevue/button'
import { getWalkthroughEmbedUrl, getWalkthroughPrefillFromStore, useWalkthroughBooking, WALKTHROUGH_SCHEDULER_URL } from '@/composables/useWalkthroughBooking'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
import './support-dialog.css'

/**
 * Opens a standalone "book a walkthrough" dialog, reachable proactively from the Help & Support
 * menu rather than only as an offshoot of switching back to Classic. Unlike the classic-console
 * revert flow, there's nothing to resolve here -- the person just closes it when they're done.
 */
export function promptScheduleWalkthrough (dialog: DialogServiceMethods): void {
  logEvent('walkthrough_dialog_shown', { context: 'help_menu' })

  const DialogContent = defineComponent({
    setup () {
      const { booked } = useWalkthroughBooking('help_menu')
      const embedUrl = getWalkthroughEmbedUrl(getWalkthroughPrefillFromStore())

      return () => (
        <div class="support-dialog">
          <div class="feedback-step feedback-step-wide">
            <div class="tw:mb-3 tw:text-sm">
              <div class="tw:mb-1">
                Pick a time that works for you. A support agent will screen-share and walk you
                through anything in the console.
              </div>
              <a href={WALKTHROUGH_SCHEDULER_URL} target="_blank" rel="noopener" class="tw:text-xs">
                Open the scheduler in a new tab
                <font-awesome-icon icon="arrow-up-right-from-square" class="tw:ml-1" />
              </a>
            </div>

            <div class="scheduler-frame-wrapper">
              <iframe src={embedUrl} title="Schedule a console walkthrough" />
            </div>

            { booked.value &&
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-3 tw:text-sm">
              <font-awesome-icon icon="circle-check" class="tw:text-green-600" />
              <span>Great — you'll get a calendar invite by email.</span>
            </div>
            }

            <div class="tw:flex tw:justify-end tw:mt-4">
              <Button type="button" size="small" label="Close" onClick={() => dialogRef.close()} />
            </div>
          </div>
        </div>
      )
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: 'Book a walkthrough',
      pt: {
        root: 'support-dialog-shell tw:!overflow-hidden tw:!rounded-xl'
      },
      modal: true,
      closable: true,
      dismissableMask: true,
      closeOnEscape: true
    }
  })
}
